import type { DeliveryMethodKind } from "@dransay/api/models";
import { useSyncExternalStore } from "react";
import type { PharmacyPickerItem } from "~/features/products/pharmacy-picker/pharmacy-picker.types";

export const PHARMACY_PICKER_SORT = {
	AZ: "az",
	ZA: "za",
	FREE_FROM: "freeFrom",
	DISTANCE: "distance",
	PRICE_ASC: "priceAsc",
} as const;

export type PharmacyPickerSort =
	(typeof PHARMACY_PICKER_SORT)[keyof typeof PHARMACY_PICKER_SORT];

/**
 * Which options a sort panel offers. Figma "Sort by" gives each surface a
 * different set, so this cannot be derived from the delivery method alone:
 *
 * - list view: A-Z, Z-A, order value — plus Entfernung for pickup/courier once
 *   an address is set
 * - PDP: A-Z, Z-A, then Entfernung (pickup with an address) or order value
 *   (shipping/courier), and always price low-to-high
 */
export type PharmacyPickerSortContext = {
	surface: "list" | "pdp";
	deliveryMethodKind?: DeliveryMethodKind | null;
	hasAddress?: boolean;
};

const DEFAULT_SORT_CONTEXT: PharmacyPickerSortContext = { surface: "list" };

const isMapDeliveryKind = (kind: DeliveryMethodKind | null | undefined) =>
	kind === "pickup" || kind === "courier";

// PDP keeps the price order it had before the picker existed; the list has no
// price to sort by.
const getDefaultPharmacyPickerSort = (
	context: PharmacyPickerSortContext,
): PharmacyPickerSort =>
	context.surface === "pdp"
		? PHARMACY_PICKER_SORT.PRICE_ASC
		: PHARMACY_PICKER_SORT.AZ;

const listeners = new Set<() => void>();
// null until the user picks a sort, so each surface applies its own default.
let currentSort: PharmacyPickerSort | null = null;

const emit = (): void => {
	for (const listener of listeners) listener();
};

export const getPharmacyPickerSort = (): PharmacyPickerSort | null =>
	currentSort;

export const setPharmacyPickerSort = (sort: PharmacyPickerSort): void => {
	if (currentSort === sort) return;
	currentSort = sort;
	emit();
};

export const resetPharmacyPickerSort = (): void => {
	currentSort = null;
	emit();
};

export const subscribePharmacyPickerSort = (
	listener: () => void,
): (() => void) => {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
};

const listSortOptions = (
	context: PharmacyPickerSortContext,
): PharmacyPickerSort[] => {
	const options: PharmacyPickerSort[] = [
		PHARMACY_PICKER_SORT.AZ,
		PHARMACY_PICKER_SORT.ZA,
		PHARMACY_PICKER_SORT.FREE_FROM,
	];
	if (isMapDeliveryKind(context.deliveryMethodKind) && context.hasAddress) {
		options.push(PHARMACY_PICKER_SORT.DISTANCE);
	}
	return options;
};

const pdpSortOptions = (
	context: PharmacyPickerSortContext,
): PharmacyPickerSort[] => {
	const options: PharmacyPickerSort[] = [
		PHARMACY_PICKER_SORT.AZ,
		PHARMACY_PICKER_SORT.ZA,
	];
	if (context.deliveryMethodKind === "pickup") {
		// No address, no distance sort.
		if (context.hasAddress) options.push(PHARMACY_PICKER_SORT.DISTANCE);
	} else {
		options.push(PHARMACY_PICKER_SORT.FREE_FROM);
	}
	options.push(PHARMACY_PICKER_SORT.PRICE_ASC);
	return options;
};

export const getPharmacyPickerSortOptions = (
	context: PharmacyPickerSortContext = DEFAULT_SORT_CONTEXT,
): PharmacyPickerSort[] =>
	context.surface === "pdp"
		? pdpSortOptions(context)
		: listSortOptions(context);

/**
 * The preference is shared across surfaces, so a sort chosen where it was
 * offered can be active where it is not. Fall back rather than sort by a
 * criterion the panel no longer lists.
 */
export const resolvePharmacyPickerSort = (
	sort: PharmacyPickerSort | null,
	context: PharmacyPickerSortContext = DEFAULT_SORT_CONTEXT,
): PharmacyPickerSort =>
	sort != null && getPharmacyPickerSortOptions(context).includes(sort)
		? sort
		: getDefaultPharmacyPickerSort(context);

const compareNullableAsc = (
	left: number | null,
	right: number | null,
): number => {
	if (left == null && right == null) return 0;
	if (left == null) return 1;
	if (right == null) return -1;
	return left - right;
};

const compareName = (
	left: PharmacyPickerItem,
	right: PharmacyPickerItem,
): number =>
	left.name.localeCompare(right.name, undefined, { sensitivity: "base" });

const pharmacyDistanceMeters = (item: PharmacyPickerItem): number | null => {
	if (item.distanceMeters != null && Number.isFinite(item.distanceMeters)) {
		return item.distanceMeters;
	}
	if (item.distanceKm != null && Number.isFinite(item.distanceKm)) {
		return item.distanceKm * 1000;
	}
	return null;
};

export const sortPharmacyPickerItems = (
	items: readonly PharmacyPickerItem[],
	sort: PharmacyPickerSort,
): PharmacyPickerItem[] => {
	if (sort === PHARMACY_PICKER_SORT.AZ) {
		return [...items].sort(compareName);
	}
	if (sort === PHARMACY_PICKER_SORT.ZA) {
		return [...items].sort((left, right) => compareName(right, left));
	}
	if (sort === PHARMACY_PICKER_SORT.FREE_FROM) {
		return [...items].sort((left, right) => {
			const byFreeFrom = compareNullableAsc(
				left.freeFromCents ?? null,
				right.freeFromCents ?? null,
			);
			return byFreeFrom !== 0 ? byFreeFrom : compareName(left, right);
		});
	}
	if (sort === PHARMACY_PICKER_SORT.PRICE_ASC) {
		// No name tie-break: the PDP shuffles same-price runs before this stable
		// sort, so equally priced pharmacies share the top spots.
		return [...items].sort((left, right) =>
			compareNullableAsc(left.price ?? null, right.price ?? null),
		);
	}
	if (sort === PHARMACY_PICKER_SORT.DISTANCE) {
		return [...items].sort((left, right) => {
			const byDistance = compareNullableAsc(
				pharmacyDistanceMeters(left),
				pharmacyDistanceMeters(right),
			);
			return byDistance !== 0 ? byDistance : compareName(left, right);
		});
	}
	return [...items];
};

export const usePharmacyPickerSort = (): [
	PharmacyPickerSort | null,
	(sort: PharmacyPickerSort) => void,
] => {
	const sort = useSyncExternalStore(
		subscribePharmacyPickerSort,
		getPharmacyPickerSort,
		getPharmacyPickerSort,
	);
	return [sort, setPharmacyPickerSort];
};
